"use client";

import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { Check } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { handleError } from "@/lib/handle-error";
import { answeredOnSave, siteAccessKey } from "@shared/addresses/site-access";
import type { Id } from "@convex/_generated/dataModel";
import type { SiteAccess } from "@convex/schemas/validators/siteAccess.lib";

interface SaveSiteAccessProps {
  access: SiteAccess;
  addressId: Id<"addresses">;
  addressLabel: string;
  savedAccess: SiteAccess | undefined;
}

function useSaveSiteAccess({ access, addressId }: SaveSiteAccessProps) {
  const updateAddress = useMutation(api.addresses.mutations.update);
  const [pending, setPending] = useState(false);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await updateAddress({ addressId, siteAccess: answeredOnSave(access) });
      setSavedKey(siteAccessKey(access));
    } catch (thrown) {
      setError(
        handleError(thrown, {
          display: "silent",
          fallbackMessage: "Could not save the site details",
        }).message,
      );
    } finally {
      setPending(false);
    }
  };
  return { pending, justSaved: savedKey === siteAccessKey(access), error, save };
}

function matchesSaved(access: SiteAccess, savedAccess: SiteAccess | undefined) {
  const savedKey = siteAccessKey(savedAccess);
  return [access, answeredOnSave(access)].some(
    (candidate) => siteAccessKey(candidate) === savedKey,
  );
}

export function SaveSiteAccess(props: SaveSiteAccessProps) {
  const saving = useSaveSiteAccess(props);
  if (matchesSaved(props.access, props.savedAccess)) {
    if (!saving.justSaved) return null;
    return (
      <p role="status" className="flex min-h-11 items-center gap-1.5 text-sm text-muted-foreground">
        <Check aria-hidden className="size-4 text-action" />
        Saved to {props.addressLabel}
      </p>
    );
  }
  return (
    <div className="grid justify-items-start gap-1">
      <Button type="button" variant="outline" disabled={saving.pending} onClick={saving.save}>
        {saving.pending ? "Saving…" : `Save these details to ${props.addressLabel}`}
      </Button>
      {saving.error && (
        <p role="alert" className="text-sm text-destructive-strong">
          {saving.error}
        </p>
      )}
    </div>
  );
}
